import React from 'react';
import { Box, Typography } from '@mui/material';

interface PaymentStatusBoxProps {
  status: string;
  lowFontSize?: boolean;
}

const PaymentStatusBox: React.FC<PaymentStatusBoxProps> = ({ status, lowFontSize = false  }) => {
  const getColorForStatus = (status: string) => {
    if (!status) return '#ffebee';

    const normalized = status.trim().toUpperCase();
    const [frequency, tier] = normalized.split(' ');

    if (frequency === 'MONTHLY') {
      switch (tier) {
        case 'SILVER':
          return '#d1c4e9'; // Light purple
        case 'GOLD':
          return '#ffe082'; // Light amber
        case 'PLATINUM':
          return '#b2ebf2'; // Light cyan
        case 'WEB':
          return '#c8e6c9'; // Light green
        default:
          return '#d191ff'; // Generic monthly color
      }
    }

    if (frequency === 'YEARLY') {
      switch (tier) {
        case 'SILVER':
          return '#e0e0e0'; // Light gray
        case 'GOLD':
          return '#fff9c4'; // Light yellow
        case 'PLATINUM':
          return '#dcedc8'; // Light lime
        case 'WEB':
          return '#b3e5fc'; // Light blue
        default:
          return '#fff8e1'; // Generic yearly color
      }
    }

    switch (normalized) {
      case 'PAID':
        return '#e6f4ea'; // Light green
      case 'PENDING':
      case 'TRIAL':
        return '#fff9c4'; // Light yellow
      default:
        return '#ffebee'; // Light red
    }
  };

  const getTextColorForStatus = (status: string) => {
    if (!status) return '#d32f2f';

    const normalized = status.trim().toUpperCase();
    const [frequency, tier] = normalized.split(' ');

    if (frequency === 'MONTHLY') {
      switch (tier) {
        case 'SILVER':
          return '#512da8'; // Dark purple
        case 'GOLD':
          return '#ff6f00'; // Dark amber
        case 'PLATINUM':
          return '#00796b'; // Teal dark
        case 'WEB':
          return '#2e7d32'; // Dark green
        default:
          return '#552478'; // Generic monthly text color
      }
    }

    if (frequency === 'YEARLY') {
      switch (tier) {
        case 'SILVER':
          return '#424242'; // Dark gray
        case 'GOLD':
          return '#fbc02d'; // Yellow dark
        case 'PLATINUM':
          return '#558b2f'; // Olive green
        case 'WEB':
          return '#0277bd'; // Blue dark
        default:
          return '#f9a825'; // Generic yearly text color
      }
    }

    switch (normalized) {
      case 'PAID':
        return '#1e8e3e'; // Dark green
      case 'PENDING':
      case 'TRIAL':
        return '#f9a825'; // Dark yellow
      default:
        return '#d32f2f'; // Dark red
    }
  };


  return (
    <Box
      sx={{
        backgroundColor: getColorForStatus(status),
        color: getTextColorForStatus(status),
        padding: '2px 2px',
        borderRadius: '4px',
        display: 'inline-block',
        width: '80px',
        textAlign: 'center',
      }}
    >
      <Typography
        variant="body2"
        fontWeight="bold"
        sx={{ fontSize: lowFontSize ? '0.5rem' : '0.7rem' }}
      >
        {status.toUpperCase()}
      </Typography>
    </Box>
  );
};

export default PaymentStatusBox;